import { PagingResultsModel } from "./../../../../models/pagingResultsModel";
import { Component, OnInit, signal, computed } from "@angular/core";
import { Store } from "@ngrx/store";
import { State } from "../../../../core/store";
import { BaseComponent } from "src/app/core/abstracts/baseComponent";
import { Actions, ofType } from "@ngrx/effects";
import { tap } from "rxjs";
import { AlertService } from "src/app/shared/services/alert.service";
import { Router } from "@angular/router";
import { OnBoardingUploadType, OnBoardingUserListModel } from "src/app/models/onBoardingModels";
import {
  loadOnBoardingUsersList,
  completeHRFile,
  completeHRFileFailure,
  completeHRFileSuccess,
  loadOnboardingUploadTypes,
  uploadEVerifyDocument,
  uploadEVerifyDocumentFailure,
  uploadEVerifyDocumentSuccess,
} from "src/app/core/store/onboarding/onboarding.actions";
import { deepClone } from "src/app/helpers/utils";

@Component({
    selector: "app-onboarding-manage-list",
    templateUrl: "./onboarding-manage-list.component.html",
    styleUrls: ["./onboarding-manage-list.component.scss"],
    standalone: false
})
export class OnboardingManageListComponent
  extends BaseComponent
  implements OnInit
{
  onBoardingUsers: PagingResultsModel<OnBoardingUserListModel>;
  canViewOnBoarding: boolean;
  loadingOnBoardingUsers = false;
  filter: any;

  yesNoAllOptions = [
    { label: "All", value: null },
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  employeeFirstNameFilter: string;
  employeeLastNameFilter: string;
  consentFilter?: boolean = null;
  allUploadedFilter?: boolean = null;
  uploadApprovedFilter?: boolean = null;
  i9SignedFilter?: boolean = null;
  i9ApprovedFilter?: boolean = null;
  employeeInfoFilter?: boolean = null;
  allDocsSignedFilter?: boolean = null;
  allDocsApprovedFilter?: boolean = null;
  finishedFilter?: boolean = null;
  orientationDateFilter;
  loading;
  displayCompleteHRDialog;
  onBoardingUserForCompleteHR;
  eVerifyCompletionDate;
  eVerifyDocumentFiles: File[] = [];
  pendingCompleteHREVerifyDocumentFiles: File[] = [];
  pendingCompleteHREVerifyUserId?: number;
  uploadTypes: OnBoardingUploadType[] = [];
  uploadingEVerifyDocument = false;
  terminatedEmployeesFilter = false;
  inactiveUsersFilter = false;

  totalRecords = signal<number>(0);
  dropdownOptions = computed(() => {
    var total = this.totalRecords();
    return [
      { label: '20', value: 20 },
      { label: '50', value: 50 },
      { label: '100', value: 100 },
      { label: '200', value: 200 },
      { label: '500', value: 500 },
      { label: 'All', value: total > 0 ? total : 20 } 
    ];
  });
  constructor(
    private actions$: Actions,
    private alertService: AlertService,
    private store: Store<State>,
    private router: Router,
  ) {
    super();
  }

  ngOnInit() {
    this.subs.push(
      // Check if user is an HR manager
      this.store
        .select((s) => s.accountState.user?.permissionIDs)
        .subscribe((permissionIDs) => {
          this.canViewOnBoarding =
            permissionIDs &&
            !!permissionIDs.find((p) => p === 3059 || p === 3060); //view on boarding, manage on boarding
        }),
      this.store
        .select((s) => s.onboardingState.onBoardingUsers)
        .subscribe((onBoardingUsers) => {
          if (onBoardingUsers) {
            this.onBoardingUsers = onBoardingUsers;
            this.totalRecords.set(onBoardingUsers.totalRecords);
            this.loading = false;
          }
        }),

      this.store
        .select((s) => s.onboardingState.loadingOnBoardingUsers)
        .subscribe((loadingOnBoardingUsers) => {
          this.loadingOnBoardingUsers = loadingOnBoardingUsers;
        }),
      this.store
        .select((s) => s.onboardingState.uploadTypes)
        .subscribe((uploadTypes) => {
          this.uploadTypes = uploadTypes ?? [];
        }),
      this.actions$
        .pipe(
          ofType(completeHRFileSuccess),
          tap((action) => {
            if (
              this.pendingCompleteHREVerifyDocumentFiles.length &&
              Number(action.userId) === Number(this.pendingCompleteHREVerifyUserId)
            ) {
              this.uploadSelectedEVerifyDocument(
                action.userId,
                this.pendingCompleteHREVerifyDocumentFiles,
              );
            } else {
              this.pendingCompleteHREVerifyDocumentFiles = [];
              this.pendingCompleteHREVerifyUserId = null;
            }
            this.closeCompleteHR();
            this.loadPage(this.filter);
          }),
        )
        .subscribe(),
      this.actions$
        .pipe(
          ofType(uploadEVerifyDocumentSuccess),
          tap((action) => {
            if (Number(action.userId) === Number(this.pendingCompleteHREVerifyUserId) && this.uploadingEVerifyDocument) {
              this.uploadingEVerifyDocument = false;
              this.pendingCompleteHREVerifyDocumentFiles = [];
              this.pendingCompleteHREVerifyUserId = null;
              this.alertService.success("E-Verify document uploaded.");
            }
          }),
        )
        .subscribe(),
      this.actions$
        .pipe(
          ofType(completeHRFileFailure),
          tap((action) => {
            this.pendingCompleteHREVerifyDocumentFiles = [];
            this.pendingCompleteHREVerifyUserId = null;
          }),
        )
        .subscribe(),
      this.actions$
        .pipe(
          ofType(uploadEVerifyDocumentFailure),
          tap((action) => {
            this.uploadingEVerifyDocument = false;
            this.pendingCompleteHREVerifyDocumentFiles = [];
            this.pendingCompleteHREVerifyUserId = null;
          }),
        )
        .subscribe(),
    );
    this.store.dispatch(loadOnboardingUploadTypes());
  }

  loadPage(event) {
    if (!this.loading) {
      this.loading = true;
      this.filter = deepClone(event);
      this.filter.filters.employeeFirstNameFilter = {
        Name: "employeeFirstNameFilter",
        Value: { value: this.employeeFirstNameFilter },
      };
      this.filter.filters.employeeLastNameFilter = {
        Name: "employeeLastNameFilter",
        Value: { value: this.employeeLastNameFilter },
      };
      this.filter.filters.consentFilter = {
        Name: "consentFilter",
        Value: { value: this.consentFilter },
      };
      this.filter.filters.allUploadedFilter = {
        Name: "allUploadedFilter",
        Value: { value: this.allUploadedFilter },
      };
      this.filter.filters.uploadApprovedFilter = {
        Name: "uploadApprovedFilter",
        Value: { value: this.uploadApprovedFilter },
      };
      this.filter.filters.i9SignedFilter = {
        Name: "i9SignedFilter",
        Value: { value: this.i9SignedFilter },
      };
      this.filter.filters.i9ApprovedFilter = {
        Name: "i9ApprovedFilter",
        Value: { value: this.i9ApprovedFilter },
      };
      this.filter.filters.employeeInfoFilter = {
        Name: "employeeInfoFilter",
        Value: { value: this.employeeInfoFilter },
      };
      this.filter.filters.allDocsSignedFilter = {
        Name: "allDocsSignedFilter",
        Value: { value: this.allDocsSignedFilter },
      };
      this.filter.filters.allDocsApprovedFilter = {
        Name: "allDocsApprovedFilter",
        Value: { value: this.allDocsApprovedFilter },
      };
      this.filter.filters.finishedFilter = {
        Name: "finishedFilter",
        Value: { value: this.finishedFilter },
      };
      if (this.orientationDateFilter) {
        this.filter.filters.orientationDateFilterStart = {
          Name: "orientationDateFilterStart",
          Value: { value: this.orientationDateFilter[0] },
        };
        this.filter.filters.orientationDateFilterEnd = {
          Name: "orientationDateFilterEnd",
          Value: { value: this.orientationDateFilter[1] },
        };
      } else {
        this.filter.filters.orientationDateFilterEnd = {
          Name: "orientationDateFilterEnd",
          Value: { value: null },
        };
      }
      this.filter.filters.terminatedEmployeesFilter = {
        Name: "terminatedEmployeesFilter",
        Value: { value: this.terminatedEmployeesFilter },
      };
      this.filter.filters.inactiveUsersFilter = {
        Name: "inactiveUsersFilter",
        Value: { value: this.inactiveUsersFilter },
      };

      this.store.dispatch(
        loadOnBoardingUsersList({
          filter: deepClone(this.filter),
        }),
      );
    }

    return event;
  }

  seeUserDetails(onBoardingUser: OnBoardingUserListModel) {
    this.router.navigate([
      "/onboarding-manage/detail/" + onBoardingUser.user.id,
    ]);
  }

  changedFilters() {
    this.loadPage(this.filter);
  }

  completeHR(onBoardingUser: OnBoardingUserListModel) {
    this.alertService
      .confirm(
        "Are you sure?",
        "By completing the HR File, this user will not be listed as Onboarding anymore and will be moved to the Employee File. Are you sure?",
      )
      .subscribe(
        (anwser) => {
          if (anwser) {
            if (this.eVerifyDocumentFiles.length && !this.otherUploadType?.id) {
              this.alertService.error("Unable to upload the E-Verify document because the Other document type is not available.");
              return;
            }

            this.pendingCompleteHREVerifyDocumentFiles = Object.assign([], this.eVerifyDocumentFiles);
            this.pendingCompleteHREVerifyUserId = onBoardingUser.user.id;
            this.store.dispatch(
              completeHRFile({
                userId: onBoardingUser.user.id,
                eVerifyCompletionDate: this.eVerifyCompletionDate,
              }),
            );
          }
        },
        (error) => {},
      );
  }

  openCompleteHR(onBoardingUser: OnBoardingUserListModel) {
    this.displayCompleteHRDialog = true;
    this.onBoardingUserForCompleteHR = onBoardingUser;
    this.eVerifyDocumentFiles = [];
    this.eVerifyCompletionDate = onBoardingUser.employee.eVerifyCompletionDate
      ? new Date(onBoardingUser.employee.eVerifyCompletionDate)
      : null;
  }
  closeCompleteHR() {
    this.displayCompleteHRDialog = false;
    this.onBoardingUserForCompleteHR = null;
    this.eVerifyCompletionDate = null;
    this.eVerifyDocumentFiles = [];
  }

  showCompleteHRButton(onBoardingUser: OnBoardingUserListModel) {
    return (
      onBoardingUser.onboarding &&
      onBoardingUser.onboarding.consentDate &&
      onBoardingUser.allUploaded &&
      onBoardingUser.onboarding.uploadedDocsApprovedDate &&
      onBoardingUser.i9FormSigned &&
      onBoardingUser.onboarding.i9ApprovedDate &&
      onBoardingUser.onboarding.employeeInfoFilled &&
      onBoardingUser.allDocsSigned &&
      onBoardingUser.onboarding.allDocumentsSignedApprovedDate &&
      onBoardingUser.onboarding.showedUpOnOrientation
    );
  }

  seeReferences(onBoardingUser: OnBoardingUserListModel) {
    this.router.navigate(
      [
        "/prospective-candidate/employee-references/" +
          onBoardingUser.prospectiveCandidateID,
      ],
      { queryParams: { backTo: "/onboarding-manage/list" } },
    );
  }

  get otherUploadType() {
    return this.uploadTypes?.find((uploadType) => uploadType.docType === "Other");
  }

  changeEVerifyDocumentFiles(fileInput: HTMLInputElement) {
    const files = Array.from(fileInput.files || []);
    if (this.hasUnsupportedWordDocument(files)) {
      this.alertService.error("Word documents are not allowed. Please upload a supported file such as PDF, JPG, or PNG.");
      fileInput.value = "";
      this.eVerifyDocumentFiles = [];
      return;
    }

    this.eVerifyDocumentFiles = files;
  }

  clearEVerifyDocumentFiles(fileInput?: HTMLInputElement) {
    this.eVerifyDocumentFiles = [];
    if (fileInput) {
      fileInput.value = "";
    }
  }

  private uploadSelectedEVerifyDocument(userId: number, files: File[]) {
    if (!files.length) {
      return;
    }

    if (!this.otherUploadType?.id) {
      this.alertService.error("Unable to upload the E-Verify document because the Other document type is not available.");
      return;
    }

    this.uploadingEVerifyDocument = true;
    this.store.dispatch(uploadEVerifyDocument({
      docTypeID: this.otherUploadType.id.toString(),
      files,
      userId,
    }));
  }

  private hasUnsupportedWordDocument(files: File[]) {
    return files.some((file) => {
      const fileName = file.name.toLowerCase();
      return fileName.endsWith(".doc") || fileName.endsWith(".docx");
    });
  }
}
